import { Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardSignature, Edit } from 'lucide-react';
import { convertISODateToDDMMYYYY, convertISODateToMMDDYYYY, convertISOToTime } from '@/lib/utils';
import KeyValueListDisplay from '@/components/KeyValueDisplay';
import { isAdminOrSupervisor } from '@/services/userService';
import { useUserStore } from '@/stores/user';
import type { HiAttemptGetResponse } from './hiAttempt.schema';
import type { HIAttemptEmailLog, HIWorkOrderComment } from '@/types/homeInspection.types';
import { EmailStatus } from '@/types/types';
import { EmailLogStatusLabel } from '@/utils/constant';

type Props = {
  workOrderNumber: string;
  attempts: HiAttemptGetResponse[];
  emailComments: HIWorkOrderComment[];
  /** Prefer server email logs when API exposes them; empty until then (API gap). */
  emailLogs?: HIAttemptEmailLog[];
};

const isWithinEditableWindow = (attempt: HiAttemptGetResponse): boolean => {
  const createdAt = new Date(attempt.createdAt);
  const editableUntil = new Date(createdAt);
  editableUntil.setFullYear(editableUntil.getFullYear() + 1);
  return new Date() <= editableUntil;
};

const mapLogStatusLabel = (status: HIAttemptEmailLog['status']): string => {
  if (status === 'SUCCESS' || status === EmailStatus.SENT) {
    return EmailLogStatusLabel[EmailStatus.SENT];
  }
  return EmailLogStatusLabel[EmailStatus.FAILED];
};

const isLogSuccess = (status: HIAttemptEmailLog['status']): boolean =>
  status === 'SUCCESS' || status === EmailStatus.SENT;

export const HIWorkOrderSidePanelAttempts = ({
  workOrderNumber,
  attempts,
  emailComments,
  emailLogs = [],
}: Props) => {
  const navigate = useNavigate();
  const loggedInUser = useUserStore((state) => state.user);

  const canEditAttempt = (attempt: HiAttemptGetResponse): boolean => {
    if (!loggedInUser) return false;
    if (isAdminOrSupervisor(loggedInUser)) return true;
    if (loggedInUser.teams.some((t) => t.key === 'SERVICE')) {
      return isWithinEditableWindow(attempt);
    }
    return false;
  };

  const sortedAttempts = [...attempts].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  const latestEmailLogs = [...emailLogs]
    .sort((a, b) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime())
    .slice(0, 5);

  return (
    <div className="flex flex-col gap-6 w-full px-6 py-6 text-sm">
      {latestEmailLogs.length > 0 && (
        <div className="flex flex-col gap-3 w-full">
          <span className="text-primary font-semibold">Emails Sent</span>
          <KeyValueListDisplay
            data={latestEmailLogs.map((log) => ({
              label: convertISODateToMMDDYYYY(log.sentAt) + ' ' + convertISOToTime(log.sentAt),
              value: mapLogStatusLabel(log.status),
              valueClassName: isLogSuccess(log.status) ? 'text-green-700' : 'text-error',
            }))}
          />
        </div>
      )}

      {/* Work-order comments fallback — attempt emailLogs require API exposure on HI WO. */}
      {latestEmailLogs.length === 0 && emailComments.length > 0 && (
        <div className="flex flex-col gap-3 w-full">
          <span className="text-primary font-semibold">Emails Sent</span>
          <KeyValueListDisplay
            data={emailComments.map((c) => ({
              label: convertISODateToDDMMYYYY(c.createdAt) + ' ' + convertISOToTime(c.createdAt),
              value: `${c.createdByFirstName} ${c.createdByLastName}`,
            }))}
          />
        </div>
      )}

      {sortedAttempts.map((attempt, index) => (
        <Fragment key={attempt.attemptId}>
          <div
            role="button"
            tabIndex={0}
            className="flex flex-row gap-2 justify-between p-4 rounded-lg bg-[#F5F5F5] cursor-pointer hover:bg-[#EBEBEB] transition-colors"
            onClick={() => navigate(`/home-inspection/${workOrderNumber}/attempt/${attempt.attemptId}/view`)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate(`/home-inspection/${workOrderNumber}/attempt/${attempt.attemptId}/view`);
              }
            }}
            aria-label={`View Attempt ${sortedAttempts.length - index}`}
          >
            <div className="flex flex-row gap-2">
              <div className="flex align-top">
                <ClipboardSignature className="h-5 w-5 text-primary" />
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-sm font-semibold">{`Attempt ${sortedAttempts.length - index}`}</span>
                <div className="flex flex-col gap-1 text-placeholder font-medium text-[12px]">
                  <span>
                    {`Added: ${convertISODateToDDMMYYYY(attempt.createdAt)} ${convertISOToTime(attempt.createdAt)}`}
                  </span>
                  {attempt.updatedById && (
                    <span>
                      {`Updated: ${convertISODateToDDMMYYYY(attempt.updatedAt)} ${convertISOToTime(attempt.updatedAt)}`}
                    </span>
                  )}
                </div>
              </div>
            </div>
            {canEditAttempt(attempt) && (
              <button
                type="button"
                title="Edit Attempt"
                aria-label={`Edit Attempt ${sortedAttempts.length - index}`}
                className="flex items-start text-primary hover:text-[#12375C]"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/home-inspection/${workOrderNumber}/attempt/${attempt.attemptId}/edit`);
                }}
              >
                <Edit className="h-4 w-4" />
              </button>
            )}
          </div>
        </Fragment>
      ))}

      {sortedAttempts.length === 0 && (
        <div className="w-full justify-center flex font-medium text-sm">No Updates</div>
      )}
    </div>
  );
};
